import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as MH from 'mh-client';
import { getApiClient } from '../../lib/apiClient';

/**
 * Hook for fetching user's overall portfolio across all funds.
 */
export function usePortfolios() {
    const client = getApiClient();

    return useQuery({
        queryKey: ['portfolios'],
        queryFn: async (): Promise<MH.OverallPortfolio> => {
            return await client.user.getPortfolios();
        },
        enabled: client.isLoggedIn(),
    });
}

/**
 * Hook for fetching a specific fund portfolio.
 */
export function usePortfolio(ticker: string) {
    const client = getApiClient();

    return useQuery({
        queryKey: ['portfolio', ticker],
        queryFn: async (): Promise<MH.Portfolio> => {
            return await client.user.getPortfolio(ticker);
        },
        enabled: client.isLoggedIn() && !!ticker,
    });
}

/**
 * Hook for fetching reserve share info for a fund.
 */
export function useReserveShareInfo(ticker: string) {
    const client = getApiClient();

    return useQuery({
        queryKey: ['reserveShareInfo', ticker],
        queryFn: async (): Promise<MH.ReserveShareInfoResponse> => {
            return await client.user.getReserveShareInfo(ticker);
        },
        enabled: client.isLoggedIn() && !!ticker,
    });
}

/**
 * Hook for fetching bank transfer details.
 */
export function useBankTransferDetails() {
    const client = getApiClient();

    return useQuery({
        queryKey: ['bankTransferDetails'],
        queryFn: async (): Promise<MH.BankDetailsResponse> => {
            return await client.user.getBankTransferDetails();
        },
        enabled: client.isLoggedIn(),
    });
}

/**
 * Hook for fetching payout bank details.
 */
export function usePayoutBankDetails() {
    const client = getApiClient();

    return useQuery({
        queryKey: ['payoutBankDetails'],
        queryFn: async (): Promise<MH.PayoutBankDetails> => {
            return await client.user.getPayoutBankDetails();
        },
        enabled: client.isLoggedIn(),
    });
}

/**
 * Hook for updating account holder details.
 */
export function useUpdateAccountHolderDetails() {
    const queryClient = useQueryClient();
    const client = getApiClient();

    return useMutation({
        mutationFn: async (request: MH.AccountHolderDetailsRequest) => {
            await client.user.updateAccountHolderDetails(request);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

/**
 * Hook for updating bank details.
 */
export function useUpdateBankDetails() {
    const queryClient = useQueryClient();
    const client = getApiClient();

    return useMutation({
        mutationFn: async (request: MH.BankDetails) => {
            await client.user.updateBankDetails(request);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['payoutBankDetails'],
            });
            queryClient.invalidateQueries({
                queryKey: ['profile'],
            });
        },
    });
}

/**
 * Hook for updating payout threshold.
 */
export function useUpdatePayoutThreshold() {
    const queryClient = useQueryClient();
    const client = getApiClient();

    return useMutation({
        mutationFn: async (request: MH.UpdateThresholdRequest) => {
            await client.user.updatePayoutThreshold(request);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payoutBankDetails'] });
            queryClient.invalidateQueries({ queryKey: ['portfolios'] });
            queryClient.invalidateQueries({ queryKey: ['portfolio'] });
        },
    });
}

/**
 * Feedback type for updating survey status.
 */
export type FeedbackType =
    | 'none'
    | 'firstCompletedTransaction'
    | 'firstCancelledTransaction';

/**
 * Hook for updating feedback survey status.
 */
export function useUpdateFeedbackStatus() {
    const queryClient = useQueryClient();
    const client = getApiClient();

    return useMutation({
        mutationFn: async (feedbackType: FeedbackType) => {
            const enumValue = {
                none: MH.ApiFeedbackSurveyTypeEnum.none,
                firstCompletedTransaction:
                    MH.ApiFeedbackSurveyTypeEnum.firstCompletedTransaction,
                firstCancelledTransaction:
                    MH.ApiFeedbackSurveyTypeEnum.firstCancelledTransaction,
            }[feedbackType];

            await client.user.updateFeedbackStatus(
                new MH.FeedbackSurveyStatusRequest(enumValue)
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}
